import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Upload, Trash2, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { platformApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";
import { usePlatformBranding, resolveBrandAsset } from "../../contexts/PlatformBrandingContext";
import { DInput } from "./ui";

const err = (e, fb) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || fb);

export default function PlatformBrandingCard() {
  const { refresh } = usePlatformBranding();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { setForm(await platformApi.getBranding()); }
    catch (e) { err(e, "Failed to load branding."); }
  };
  useEffect(() => { let a = true; (async () => { await Promise.resolve(); if (a) load(); })(); return () => { a = false; }; }, []);

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo too large (max 2 MB)."); return; }
    setUploading(true);
    try {
      const updated = await platformApi.uploadBrandingLogo(file);
      setForm(updated);
      toast.success("Logo updated.");
      refresh();
    } catch (e2) { err(e2, "Could not upload logo."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    setUploading(true);
    try {
      const updated = await platformApi.removeBrandingLogo();
      setForm(updated);
      toast.success("Logo reset to default.");
      refresh();
    } catch (e2) { err(e2, "Could not remove logo."); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form?.brandName?.trim()) { toast.error("Brand name is required."); return; }
    setSaving(true);
    try {
      await platformApi.updateBranding({
        brandName: form.brandName,
        tagline: form.tagline,
        poweredByLabel: form.poweredByLabel,
        poweredByUrl: form.poweredByUrl,
      });
      toast.success("Branding saved.");
      refresh();
    } catch (e2) { err(e2, "Could not save branding."); }
    finally { setSaving(false); }
  };

  if (!form) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 text-cyan-400 animate-spin" /></div>;
  }

  const logoSrc = resolveBrandAsset(form.sidebarLogoUrl) || "/brand-logo.png";

  return (
    <div className="max-w-2xl space-y-5" data-testid="platform-branding-card">
      <div>
        <h2 className="text-[15px] font-bold text-slate-100">Product branding</h2>
        <p className="text-[12.5px] text-slate-400 mt-0.5">Controls the sidebar logo &amp; name shown to every tenant, and the “Powered by” label printed on all estimates &amp; invoices.</p>
      </div>

      {/* Sidebar logo */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-300 mb-3"><ImageIcon className="h-4 w-4 text-cyan-400" /> Sidebar logo</div>
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
            <img src={logoSrc} alt="brand" className="h-full w-full object-contain" data-testid="platform-branding-logo-preview" />
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onPickLogo} data-testid="platform-branding-logo-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="platform-branding-upload-btn"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-60">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload logo
            </button>
            <button onClick={removeLogo} disabled={uploading} data-testid="platform-branding-remove-btn"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-[13px] font-medium">
              <Trash2 className="h-4 w-4" /> Reset
            </button>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">PNG, JPG, WEBP or SVG. Max 2 MB. Applies to every tenant sidebar.</p>
      </div>

      {/* Text fields */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-4 grid sm:grid-cols-2 gap-4">
        <DInput label="Brand name" value={form.brandName || ""} onChange={(e) => set("brandName")(e.target.value)} testid="platform-branding-name" placeholder="ServiceGuru Pro" />
        <DInput label="Tagline" value={form.tagline || ""} onChange={(e) => set("tagline")(e.target.value)} testid="platform-branding-tagline" placeholder="Field Ops" />
        <DInput label='"Powered by" label' value={form.poweredByLabel || ""} onChange={(e) => set("poweredByLabel")(e.target.value)} testid="platform-branding-poweredby-label" placeholder="Powered by ServiceGuru Pro" />
        <DInput label='"Powered by" link URL' value={form.poweredByUrl || ""} onChange={(e) => set("poweredByUrl")(e.target.value)} testid="platform-branding-poweredby-url" placeholder="https://servicegurupro.com" />
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="platform-branding-save-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save branding
        </button>
        {form.poweredByUrl && (
          <a href={form.poweredByUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12.5px] text-cyan-300 hover:text-cyan-200">
            Preview link <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}
